import PropTypes from 'prop-types';
import { bookCompletions } from '../../utils/communityBooks';
import './BookCompletionMeters.css';

const formatNumber = (value) => new Intl.NumberFormat().format(value ?? 0);

/**
 * Every book of the Bible with a meter of how many times the community has
 * read all of it this season, against the complete-Bible goal. A book is read
 * through once every one of its chapters has been, so its count is the fewest
 * reads of any of its chapters.
 *
 * Without a goal the meters scale to the most-read book instead.
 */
const BookCompletionMeters = ({ chapters, chapterReadCounts, goal = null }) => {
  const books = bookCompletions(chapters, chapterReadCounts);
  if (books.length === 0) return null;

  const scale = goal || Math.max(1, ...books.map(book => book.completions));

  const renderBook = (book) => {
    const percent = Math.min(100, Math.round((book.completions / scale) * 100));
    const reached = goal !== null && book.completions >= goal;
    const label = `${book.book}: read through ${book.completions} time${book.completions === 1 ? '' : 's'}${goal ? ` of ${goal}` : ''}`;

    return (
      <li key={book.book} className={`book-meter${reached ? ' reached' : ''}`}>
        <span className="book-meter-name">{book.book}</span>
        <span
          className="book-meter-track"
          role="progressbar"
          aria-valuenow={Math.min(book.completions, scale)}
          aria-valuemin={0}
          aria-valuemax={scale}
          aria-label={label}
        >
          <span className="book-meter-fill" style={{ width: `${percent}%` }} />
        </span>
        <span className="book-meter-count">
          {formatNumber(book.completions)}
          {goal && <span className="book-meter-goal">/{formatNumber(goal)}</span>}
        </span>
      </li>
    );
  };

  const testaments = [
    { key: 'Old', title: 'Old Testament' },
    { key: 'New', title: 'New Testament' }
  ];

  return (
    <section className="book-meters" aria-labelledby="book-meters-title">
      <div className="book-meters-header">
        <h2 id="book-meters-title">Book by book</h2>
        <p>
          {goal
            ? `Times each book has been read all the way through, toward our goal of ${formatNumber(goal)} complete Bibles.`
            : 'Times each book has been read all the way through this season.'}
        </p>
      </div>

      <div className="book-meters-columns">
        {testaments.map(({ key, title }) => {
          const testamentBooks = books.filter(book => book.testament === key);
          if (testamentBooks.length === 0) return null;
          return (
            <div key={key} className="book-meters-testament">
              <h3>{title}</h3>
              <ul>{testamentBooks.map(renderBook)}</ul>
            </div>
          );
        })}
      </div>
    </section>
  );
};

BookCompletionMeters.propTypes = {
  chapters: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    book: PropTypes.string,
    testament: PropTypes.string
  })).isRequired,
  chapterReadCounts: PropTypes.objectOf(PropTypes.number).isRequired,
  goal: PropTypes.number
};

export default BookCompletionMeters;
